import { Box, Inline, xcss } from '@atlaskit/primitives';
import ContentLink from '@databases/content-link/ContentLink';
import { type ContentReference } from '@databases/types/confluence';
import { DatabaseFieldType } from '@databases/types/database';
import {
  MultiValueChangeType,
  type ValueChange
} from '@databases/utils/database/changesets';
import React, { type FunctionComponent, useCallback } from 'react';

import { FieldValueRemoveButton, filterOutRemovedValue } from '../popover-edit-common';

import { type ExtendedPageReference, type Value } from './types';

interface PageLinkValueProps {
  resolvedValue: Value;
  resolvedValues: Value[];
  onChange: (value: Value[], change: ValueChange | null) => void;
  autoFocus: boolean | undefined;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
}

export const PageLinkValue: FunctionComponent<PageLinkValueProps> = ({
  resolvedValue,
  resolvedValues,
  onChange,
  autoFocus,
  addDataSectionRef
}) => {
  const pageLinkValueIndex = resolvedValues.findIndex(
    (value) =>
      value &&
      'contentId' in value &&
      'contentId' in resolvedValue &&
      value.contentId === resolvedValue.contentId &&
      'ari' in value &&
      'ari' in resolvedValue &&
      value.ari === resolvedValue.ari &&
      value._id === resolvedValue._id
  );
  const remainingValues = filterOutRemovedValue({
    currentValues: resolvedValues,
    indexToRemove: pageLinkValueIndex
  });

  const onItemDelete = useCallback(() => {
    onChange(remainingValues, {
      type: DatabaseFieldType.PAGE_LINK,
      delta: {
        type: MultiValueChangeType.REMOVE,
        index: pageLinkValueIndex
      }
    });
    // focus the select input after each item removal
    addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
  }, [addDataSectionRef, onChange, pageLinkValueIndex, remainingValues]);

  return (
    <Inline
      xcss={xcss({
        display: 'inline-flex',
        maxWidth: '100%',
        alignItems: 'end'
      })}
      testId={'page-link-value'}
    >
      <Box
        xcss={xcss({
          marginRight: 'space.075',
          minWidth: '0'
        })}
      >
        <ContentLink
          contentId={
            (resolvedValue as ExtendedPageReference | ContentReference).contentId
          }
          noFocus
        />
      </Box>
      <FieldValueRemoveButton
        testId={'page-link-remove-button'}
        onItemDelete={onItemDelete}
        autoFocus={autoFocus}
      />
    </Inline>
  );
};
